import { Box, TextField } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DicomVRMaxLength } from '~/domain/dicom/dicomConstants';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { formatReviewDateTime } from '~/utils/io/files/formatDate';
import ApprovalStatusDropdown from './ApprovalStatusDropdown';
import StructureSetLabelField from './StructureSetLabelField';

const dash = '\u2014';

const StructureSetSettings = () => {
    const intl = useIntl();
    const { structureSet, setStructureSetDescription, setStructureSetLabel } =
        useStructureSet();

    const handleDescriptionChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        event.stopPropagation();
        setStructureSetDescription(event.target.value);
    };

    if (!structureSet) {
        return <></>;
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} width={300}>
            <Box
                display="grid"
                gridTemplateColumns="auto 1fr"
                gap="3px 6px"
                alignItems="baseline"
                sx={{
                    '& .MuiTypography-root': {
                        fontSize: 11,
                        lineHeight: 1.2,
                    },
                }}
            >
                {structureSet.generationModel && (
                    <>
                        <LabelText>
                            <FormattedMessage id="generationModel" />
                        </LabelText>
                        <ValueText component="span">
                            {`${structureSet.generationModel.label} v.${structureSet.generationModel.version}`}
                        </ValueText>
                    </>
                )}

                <LabelText>
                    <FormattedMessage id="module" />
                </LabelText>
                <ValueText component="span">
                    {structureSet.manufacturerModelName || dash}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="manufacturer" />
                </LabelText>
                <ValueText component="span">
                    {structureSet.manufacturer || dash}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="softwareVersion" />
                </LabelText>
                <ValueText component="span">
                    {structureSet.softwareVersions || dash}
                </ValueText>

                <LabelText>
                    <FormattedMessage id="structures" />
                </LabelText>
                <ValueText component="span">
                    {structureSet.numberOfStructures}
                </ValueText>

                <LabelText>
                    {intl.formatMessage({ id: 'sopInstanceUid' })}
                </LabelText>
                <IdField
                    id={structureSet.dicomUid}
                    typographyProps={{ fontSize: 11 }}
                />

                {structureSet.reviewerName.length > 0 && (
                    <>
                        <LabelText>
                            <FormattedMessage id="reviewerName" />
                        </LabelText>
                        <ValueText component="span">
                            {structureSet.reviewerName}
                        </ValueText>

                        <LabelText>
                            <FormattedMessage id="reviewTime" />
                        </LabelText>
                        <ValueText component="span">
                            <SensitiveInfoTypography>
                                {formatReviewDateTime(
                                    structureSet.reviewDate,
                                    structureSet.reviewTime
                                )}
                            </SensitiveInfoTypography>
                        </ValueText>
                    </>
                )}
            </Box>
            <StructureSetLabelField
                onChange={setStructureSetLabel}
                structureSetLabel={structureSet.structureSetLabel}
            />
            <ApprovalStatusDropdown structureSet={structureSet} />
            <TextField
                label={intl.formatMessage({ id: 'description' })}
                placeholder={intl.formatMessage({
                    id: 'structureSetDescription',
                })}
                fullWidth
                multiline
                rows={3}
                size="small"
                value={structureSet.structureSetDescription}
                onChange={handleDescriptionChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                    maxLength: DicomVRMaxLength.TEXT,
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                }}
                data-testid="structure-set-description-field"
            />
        </Box>
    );
};

export default StructureSetSettings;
